import { css, keyframes } from '@emotion/css';
import {
  extraLargeDesktopWidth,
  largeDesktopWidth,
  m,
  mediaQueryForRange,
  nonMobileMediaQuery,
} from '@snapchat/snap-design-system-marketing';

// The designs use a breakpoint that is not used in sdsm
const smallDesktopWidth = 1024;
const smallDesktopMediaQuery = mediaQueryForRange({ min: smallDesktopWidth + 1 });

const largeDesktopAndSmallerMediaQuery = mediaQueryForRange({ max: largeDesktopWidth });

export const containerCss = css`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  /* Set font-family here or the var from the default motif is used */
  font-family: ${m('--font-family')};
  margin-bottom: ${m('--spacing-xxxxl')};
  padding-inline: ${m('--spacing-xl')};

  ${nonMobileMediaQuery} {
    /* Minus header height (64px) and footer height (66px) */
    min-height: calc(100vh - 64px - 66px);
  }

  ${smallDesktopMediaQuery} {
    margin-inline: auto;
    max-width: ${extraLargeDesktopWidth}px;
    padding-inline: ${m('--spacing-xxxxl')};
    width: 100%;
  }
`;

export const carouselOverrideStylesCss = css`
  /* Override the desktop grid gap so that smaller desktop screens have the same gap as mobile */
  ${largeDesktopAndSmallerMediaQuery} {
    --carousel-card-desktop-grid-gap: ${m('--carousel-card-mobile-grid-gap')};
  }

  .sdsm-carousel {
    /**
     * Remove the vertical spacing from the carousel container to allow the bottom of the cards
     * to be closer to the bottom of the screen. This is because there will be extra space below
     * the cards if there are no pagination dots.
     * */
    & > div > div {
      margin-block: 0;
    }

    /* Add spacing to the top of the pagination dots to make up for the removed container spacing above. */
    & > nav {
      margin-top: ${m('--spacing-xl')};
    }

    /* Remove the min-height and absolute positioning from the carousel card text container */
    .sdsm-carousel-card-text {
      min-height: unset;
      position: unset;

      p {
        /**
         * The carousel changes font size for mobile and desktop,
         * but we need to increase at a different breakpoint
         * */
        ${smallDesktopMediaQuery} {
          font-size: 16px;
        }
      }
    }
  }
`;

export const titleCss = css`
  align-items: center;
  display: flex;
  flex-grow: 1;
  && {
    font-size: 26px;
    font-weight: 400;
  }
  max-width: 540px;
  /* Minus header height (64px) and height of carousel title and arrow indicator (70px) */
  min-height: calc(100vh - 64px - 70px);
  padding-block: 56px;
  text-wrap: pretty;
  width: 100%;

  /* If supported, use dvh for min-height to avoid address bar covering content on mobile */
  @supports (min-height: 100dvh) {
    min-height: calc(100dvh - 64px - 70px);
  }

  ${nonMobileMediaQuery} {
    && {
      font-size: 28px;
    }
    max-width: calc(590px + (2 * ${m('--spacing-xxxl')}));
    min-height: unset;
    padding: 56px ${m('--spacing-xxxl')};
  }

  ${smallDesktopMediaQuery} {
    && {
      font-size: 44px;
    }
    max-width: calc(1040px + (2 * ${m('--spacing-xxxl')}));
  }
`;

export const carouselTitleCss = css`
  align-items: center;
  color: ${m('--block-eyebrow-color')};
  display: flex;
  flex-direction: column;
  font-size: 14px;
  font-weight: 600;
  gap: ${m('--spacing-xs')};
  justify-content: center;
  letter-spacing: -0.01em;
  line-height: 20px;
  margin-bottom: ${m('--spacing-m')};
  text-align: center;

  ${nonMobileMediaQuery} {
    margin-bottom: ${m('--spacing-xxxl')};
  }
`;

export const carouselArrowIconCss = css`
  ${nonMobileMediaQuery} {
    display: none;
  }
`;

const fadeInDownTitle = keyframes`
  from {
    opacity: 0;
    transform: translateY(-64px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInDownCarousel = keyframes`
  from {
    opacity: 0;
    transform: translateY(-48px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const animationBaseCss = css`
  animation-delay: 600ms;
  animation-duration: 800ms;
  animation-fill-mode: forwards;
  animation-timing-function: ease-in-out;
  opacity: 0; /* Start hidden immediately */

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
    transform: none;
  }
`;

export const titleAnimatedCss = css`
  ${animationBaseCss}
  animation-name: ${fadeInDownTitle};
`;

export const carouselAnimatedCss = css`
  ${animationBaseCss}
  animation-name: ${fadeInDownCarousel};
`;

// Hidden state before animation starts (when data is not yet available)
export const preAnimationCss = css`
  opacity: 0;
`;
